// Loads the current style's crate shard and the channel registry (SPEC F6).
// Online: fetch public/crate/<styleId>.json and cache it in IndexedDB.
// Offline or missing: fall back to the cached shard. No shard at all means
// "empty" (the crate build hasn't run for this style yet).
// Data older than 30 days (YouTube policy) is never shown: it is dropped from
// IndexedDB and the runtime cache, and the status is "expired" until a fresh
// shard can be fetched.

import { useEffect, useState } from 'react';
import type { Channel, StyleId } from '../../data/types';
import { listChannels } from '../../db/repos/channels';
import { deleteCrate, loadCrate, saveCrate } from '../../db/repos/dig';
import { afterFirstPaint } from '../../lib/afterFirstPaint';
import { effectiveChannels } from '../../lib/channels';
import { crateExpired, type CrateChannel, type CrateShard } from '../../lib/dig';

export interface CrateData {
  status: 'loading' | 'ready' | 'empty' | 'expired';
  shard?: CrateShard;
  channels: CrateChannel[];
  /** Local edits, for the channel editor */
  local: Channel[];
  /** No channels.json shipped at all and no local channels: the crate build has never run. */
  noCrateAtAll: boolean;
}

export async function fetchJson(path: string): Promise<unknown> {
  try {
    const res = await fetch(path);
    if (!res.ok) return undefined;
    return JSON.parse(await res.text()) as unknown; // a dev server may answer with index.html: that isn't JSON
  } catch {
    return undefined;
  }
}

export const isShard = (x: unknown): x is CrateShard =>
  typeof x === 'object' && x !== null && ((x as CrateShard).v === 1 || (x as CrateShard).v === 2) && Array.isArray((x as CrateShard).records) && Array.isArray((x as CrateShard).channels);

/** Best effort: the service worker's copy of the shards goes too, so an old one can't come back. */
export async function dropRuntimeCache(): Promise<void> {
  try {
    await globalThis.caches?.delete('crate');
  } catch {
    // no Cache Storage here (private window, old browser): IndexedDB was cleared already
  }
}

export interface ChannelRegistry {
  shipped: CrateChannel[];
  local: Channel[];
  channels: CrateChannel[];
  /** No channels.json shipped at all and no local channels: the crate build has never run. */
  noCrateAtAll: boolean;
}

/** public/crate/channels.json (the shipped registry), merged with local edits. Shared by useCrate and Dig › Channels. */
export async function loadChannelRegistry(): Promise<ChannelRegistry> {
  const shippedRaw = await fetchJson('/crate/channels.json');
  const shipped = Array.isArray(shippedRaw) ? (shippedRaw as CrateChannel[]) : [];
  const local = await listChannels().catch(() => [] as Channel[]);
  const channels = effectiveChannels(shipped, local);
  // A local channel with only a handle (not yet resolved by the crate build) has no id, so
  // effectiveChannels leaves it out of `channels` even though it still counts as "something set up".
  const unresolvedLocal = local.some((c) => !c.id);
  return { shipped, local, channels, noCrateAtAll: channels.length === 0 && !unresolvedLocal };
}

/** Dig › Channels: the shipped registry merged with local edits, reloaded on demand (after a save, import or delete). */
export function useChannelRegistry(reloadKey = 0): ChannelRegistry & { status: 'loading' | 'ready' } {
  const [loaded, setLoaded] = useState<(ChannelRegistry & { key: number }) | undefined>();

  useEffect(() => {
    let cancelled = false;
    void loadChannelRegistry().then((r) => {
      if (!cancelled) setLoaded({ ...r, key: reloadKey });
    });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return loaded?.key === reloadKey
    ? { ...loaded, status: 'ready' }
    : { status: 'loading', shipped: [], local: [], channels: [], noCrateAtAll: false };
}

/** `skip` is true in Genres mode: nothing is fetched then. */
export function useCrate(styleId: StyleId, reloadKey = 0, skip = false): CrateData {
  const [loaded, setLoaded] = useState<(CrateData & { key: string }) | undefined>();
  const key = `${styleId}:${reloadKey}`;

  useEffect(() => {
    if (skip) return undefined;
    let cancelled = false;
    // The screen paints first, then the crate loads (v1.0 quality pass)
    const cancelStart = afterFirstPaint(() => void (async () => {
      const fresh = await fetchJson(`/crate/${styleId}.json`);
      let shard: CrateShard | undefined;
      let expired = false;
      if (isShard(fresh) && !crateExpired(fresh, Date.now())) {
        shard = fresh;
        await saveCrate(styleId, fresh, Date.now()).catch(() => undefined);
      } else {
        // Offline, or the host only has an old shard: the cached copy is the last resort, if it is young enough
        expired = isShard(fresh);
        const cached = await loadCrate(styleId).catch(() => undefined);
        if (isShard(cached?.shard)) {
          if (crateExpired(cached.shard, Date.now())) expired = true;
          else if (!expired) shard = cached.shard;
        }
        if (expired) {
          await deleteCrate(styleId).catch(() => undefined);
          await dropRuntimeCache();
        }
      }
      const registry = await loadChannelRegistry();
      if (cancelled) return;
      setLoaded({
        key,
        status: shard ? 'ready' : expired ? 'expired' : 'empty',
        shard,
        channels: registry.channels,
        local: registry.local,
        noCrateAtAll: registry.noCrateAtAll,
      });
    })());
    return () => {
      cancelled = true;
      cancelStart();
    };
  }, [styleId, key, skip]);

  return !skip && loaded?.key === key ? loaded : { status: 'loading', channels: [], local: [], noCrateAtAll: false };
}
